import React, {
  ComponentType,
  useCallback,
  useId,
  useMemo,
  useState,
} from "react";

import type { Dayjs } from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import Button from "../../../shared/Button/Button";
import Select from "../../../shared/Select/Select";
import {
  formatCompactCurrencyTick,
  formatCurrency,
} from "../../../../utils/financialFormatters";
import { getAdminNow } from "../../../../utils/dateService";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../../utils/Constants";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../../utils/narrowViewportChartDates";
import {
  DEFAULT_TOP_RANKING_LIMIT,
  TOP_RANKING_BAR_ROW_HEIGHT_PX,
  TOP_RANKING_LIMIT_OPTIONS,
  TOP_RANKING_MIN_CHART_HEIGHT_PX,
  TOP_RANKING_SCROLL_THRESHOLD,
} from "../../../../utils/kpiRankingMetric";
import {
  createRankingBarDatalabelsPlugin,
  formatRankingPercentChange,
} from "../../../../utils/kpiRankingDatalabels";
import { BarChart, ChartCard, CHART_PALETTE } from "../../../shared/Charts";
import {
  getSportsbookMetricPercentChange,
  getSportsbookMetricValue,
  type SportsbookRankingRow,
  type SportsbookTopSportsData,
  type SportsbookTopSportsMetricKey,
} from "../../../../queries/sportsbook-kpis/normalizeSportsbookRankingRows";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";

import styles from "./TopFiveChart.module.css";

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

type MetricKey = SportsbookTopSportsMetricKey;

const CURRENCY_METRICS = new Set<MetricKey>([
  "turnover",
  "pnl",
  "avg_bet_amount",
]);

type UseTopRankingResult = {
  data?: SportsbookTopSportsData;
  isFetching?: boolean;
  isLoading?: boolean;
};

const NO_LIVE_DATA: UseTopRankingResult = {};

export type TopFiveChartProps = {
  title: string;
  exportFilename: string;
  /**
   * Live-data hook. Called with date range + `limit` so the BE returns the
   * requested number of ranked rows.
   */
  useData: (filters: {
    start_date: string;
    end_date: string;
    limit: number;
  }) => UseTopRankingResult;
};

const TopFiveChart: React.FC<TopFiveChartProps> = ({
  title,
  exportFilename,
  useData,
}) => {
  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.startOf("month"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.CURRENT_MONTH),
  );
  const [metric, setMetric] = useState<MetricKey>("turnover");
  const [limit, setLimit] = useState<number>(DEFAULT_TOP_RANKING_LIMIT);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const filtersId = useId();

  const currencySymbol = useCurrencySymbol() ?? "$";

  const filters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
      limit,
    }),
    [dates, limit],
  );

  const liveResult: UseTopRankingResult = (useData ?? (() => NO_LIVE_DATA))(
    filters,
  );
  const showLoading = Boolean(liveResult.isFetching || liveResult.isLoading);

  const rows = useMemo<SportsbookRankingRow[]>(
    () => liveResult.data?.byMetric[metric] ?? [],
    [liveResult.data, metric],
  );
  const showEmpty = !showLoading && rows.length === 0;
  const isScrollable = rows.length > TOP_RANKING_SCROLL_THRESHOLD;
  const chartHeight = isScrollable
    ? Math.max(
        TOP_RANKING_MIN_CHART_HEIGHT_PX,
        rows.length * TOP_RANKING_BAR_ROW_HEIGHT_PX,
      )
    : TOP_RANKING_MIN_CHART_HEIGHT_PX;

  const metricOptions: { value: MetricKey; label: string }[] = [
    { value: "turnover", label: AppStrings.TURNOVER },
    { value: "pnl", label: AppStrings.PROFIT_LOSS },
    { value: "margin_pct", label: AppStrings.MARGIN_PCT },
    { value: "total_bets", label: AppStrings.TOTAL_BETS },
    { value: "avg_bet_amount", label: AppStrings.AVG_BET_AMOUNT },
    { value: "total_users", label: AppStrings.TOTAL_ACTIVE_USERS },
  ];

  const limitOptions = TOP_RANKING_LIMIT_OPTIONS.map((value) => ({
    value,
    label: String(value),
  }));

  const formatValue = useCallback(
    (value: number) => {
      if (metric === "margin_pct") return `${value}%`;
      if (CURRENCY_METRICS.has(metric))
        return formatCurrency(value, currencySymbol);
      return Intl.NumberFormat("en-US").format(value);
    },
    [metric, currencySymbol],
  );

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> query key, which refetches.
  }, []);

  const exportRows = useMemo(
    () =>
      rows.map((r) => ({
        name: r.name,
        turnover: r.turnover.value,
        turnover_pct_change: r.turnover.percent_change,
        profit_and_loss: r.pnl.value,
        profit_and_loss_pct_change: r.pnl.percent_change,
        margin_pct: r.margin_pct.value,
        margin_pct_pct_change: r.margin_pct.percent_change,
        total_bets: r.total_bets.value,
        total_bets_pct_change: r.total_bets.percent_change,
        avg_bet_amount: r.avg_bet_amount.value,
        avg_bet_amount_pct_change: r.avg_bet_amount.percent_change,
        total_users: r.total_users.value,
        total_users_pct_change: r.total_users.percent_change,
      })),
    [rows],
  );

  const chartData = useMemo(
    () => ({
      labels: rows.map((r) => r.name),
      datasets: [
        {
          data: rows.map((r) => getSportsbookMetricValue(r, metric)),
          backgroundColor: rows.map(
            (_, idx) => CHART_PALETTE.bars[idx % CHART_PALETTE.bars.length],
          ),
          borderRadius: 4,
          maxBarThickness: 22,
        },
      ],
    }),
    [rows, metric],
  );

  const rankingDatalabelsPlugin = useMemo(
    () =>
      createRankingBarDatalabelsPlugin(
        (dataIndex) =>
          getSportsbookMetricPercentChange(rows[dataIndex], metric),
        formatValue,
      ),
    [rows, metric, formatValue],
  );

  const chartOptions = useMemo(
    () => ({
      layout: { padding: { right: 96 } },
      scales: {
        x: {
          beginAtZero: true,
          grid: { display: true },
          ticks: {
            callback: (value: number | string) =>
              CURRENCY_METRICS.has(metric)
                ? formatCompactCurrencyTick(Number(value), currencySymbol)
                : value,
          },
        },
        y: {
          grid: { display: false },
          ticks: { font: { size: 12 } },
        },
      },
      plugins: {
        tooltip: {
          displayColors: false,
          callbacks: {
            label: (item: { raw: unknown; dataIndex: number }) => {
              const valueLabel = formatValue(Number(item.raw));
              const pct = getSportsbookMetricPercentChange(
                rows[item.dataIndex],
                metric,
              );
              if (pct == null) return valueLabel;
              return `${valueLabel} (${formatRankingPercentChange(pct)})`;
            },
          },
        },
        // Dual-tone labels are drawn by `rankingDatalabelsPlugin` (white
        // value + green/red %). chartjs-plugin-datalabels can't split colors.
        datalabels: { display: false },
      },
    }),
    [currencySymbol, formatValue, rows, metric],
  );

  return (
    <ChartCard
      title={title}
      headerExtra={
        <>
          <span className={styles.filterToggle}>
            <Button
              onClick={() => setFiltersVisible((visible) => !visible)}
              aria-expanded={filtersVisible}
              aria-controls={filtersId}
              aria-label={AppStrings.PAM_REPORT_FILTERS}
            >
              <Filter size={18} aria-hidden="true" />
              {filtersVisible ? (
                <ChevronUp size={16} aria-hidden="true" />
              ) : (
                <ChevronDown size={16} aria-hidden="true" />
              )}
            </Button>
          </span>
          <div
            id={filtersId}
            className={`${styles.headerSelects}${
              filtersVisible ? "" : ` ${styles.filtersCollapsed}`
            }`}
            data-testid="sportsbook-top-ranking-filters"
          >
            <Select
              className={styles.limitSelect}
              value={limit}
              options={limitOptions}
              onChange={(value) => setLimit(Number(value))}
              aria-label={AppStrings.PAM_TOP_RANKING_LIMIT}
            />
            <Select
              className={styles.metricSelect}
              value={metric}
              options={metricOptions}
              onChange={(value) => setMetric(value as MetricKey)}
              aria-label={AppStrings.METRIC}
            />
          </div>
        </>
      }
      bodyClassName={styles.chartContainer}
      controls={
        <>
          <CustomDatePicker
            getData={getData}
            dates={dates}
            setDates={setDates}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton data={exportRows} filename={exportFilename} />
        </>
      }
    >
      {showEmpty ? (
        <p
          className={styles.emptyMessage}
          data-testid="sportsbook-top-ranking-empty"
        >
          {AppStrings.PAM_CHART_EMPTY_STATE}
        </p>
      ) : (
        <div
          className={styles.chartViewport}
          aria-label={title}
          tabIndex={isScrollable ? 0 : undefined}
          data-testid="sportsbook-top-ranking-viewport"
        >
          <div
            className={styles.chartCanvas}
            style={{ height: `${chartHeight}px` }}
            data-testid="sportsbook-top-ranking-canvas"
          >
            <BarChart
              horizontal
              data={chartData}
              optionsOverride={chartOptions}
              plugins={[rankingDatalabelsPlugin as never]}
              isLoading={showLoading}
            />
          </div>
        </div>
      )}
    </ChartCard>
  );
};

export default TopFiveChart;
